import { h, Component } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { canUser } from 'utils/roles';
import { isFocusStudio } from 'utils/studioConfig';
import { parseDate } from 'utils/utils';

import CarouselItem from 'components/editor/settings/carouselSettings/carouselItem';
import { Sortable } from 'components/sortable/sortable';

import s from 'components/editor/settings/carouselSettings/carouselSettings.sss';

export default class CarouselSettings extends Component {
	constructor(props) {
		super(props);

		this.addMovieToCarousel = this.addMovieToCarousel.bind(this);
		this.onCarouselSort = this.onCarouselSort.bind(this);
		this.removeMovieFromCarousel = this.removeMovieFromCarousel.bind(this);
		this.selectCarouselItem = this.selectCarouselItem.bind(this);
		this.sortMoviesByReleaseDate = this.sortMoviesByReleaseDate.bind(this);
		this.togglePinned = this.togglePinned.bind(this);
	}

	get availableToAdd() {
		const { movies, selectedMovies } = this.props;
		const existingIds = new Set(selectedMovies?.map(m => m.movie) || []);

		let available = Object.values(movies || {})
			.filter(m => !existingIds.has(m.id))
			.sort(this.sortMoviesByReleaseDate);

		available = available.filter(m => {
			if (m.hero?.type === 'video' && !m.hero?.desktop?.video?.url) {
				return false;
			}

			if (
				isFocusStudio() && !m?.hero?.still?.url || // Hero Still lives under 'hero.still' for Focus
				!isFocusStudio() && !m?.hero?.desktop?.still?.url // Hero Still lives under 'hero.desktop.still' for Universal
			) {
				return false;
			}

			return true;
		});

		// All future dates first (closest to farthest from now), then past dates (closest to farthest from now)
		const now = new Date();
		available.sort((a, b) => {
			const ra = parseDate(a?.dates?.release) || null;
			const rb = parseDate(b?.dates?.release) || null;
			if (!ra && !rb) return 0;
			if (!ra) return 1;
			if (!rb) return -1;
			const aPast = ra < now;
			const bPast = rb < now;
			if (aPast !== bPast) return aPast ? 1 : -1;
			return Math.abs(now - ra) - Math.abs(now - rb);
		});

		return available;
	}

	addMovieToCarousel(e) {
		const { carouselId, dataRoot } = this.props;
		const movieId = e.target.closest('[data-movie]')?.dataset.movie;
		if (!carouselId || !movieId) return;

		
		const data = { movie: movieId };
		const carouselContent = jsonPointer.get(Store.get().data, `${dataRoot || ''}/content/${carouselId}`);
		if (!carouselContent.list) {
			carouselContent.set('list', [data]);
		} else {
			carouselContent.list.push(data);
		}
	}

	onCarouselSort(e) {
		const { carouselId, dataRoot } = this.props;
		const path = `${dataRoot || ''}/content/${carouselId}`;
		const parent = jsonPointer.get(Store.get().data, `${path}`);
		const list = parent.list?.toJS() || [];
		list.splice(e.to, 0, list.splice(e.from, 1)[0]);
		parent.set('list', list);
	}

	removeMovieFromCarousel(e) {
		const { carouselId, dataRoot } = this.props;
		const movieId = e.target.closest('[data-movie]')?.dataset.movie;
		if (!carouselId || !movieId) return;

		const list = jsonPointer.get(Store.get().data, `${dataRoot || ''}/content/${carouselId}/list`);
		const index = list?.findIndex(item => item.movie === movieId);
		if (!list || index === -1) return;
		list.splice(index, 1);
	}

	selectCarouselItem(e) {
		const { carouselId, dataRoot, onItemSelect, sortable } = this.props;
		const movieId = e.target.closest('[data-movie]')?.dataset.movie;
		if (!onItemSelect || !carouselId || !movieId) return;

		if (sortable) {
			const list = jsonPointer.get(Store.get().data, `${dataRoot || ''}/content/${carouselId}/list`);
			const index = list?.findIndex(item => item.movie === movieId);
			if (list && index !== -1) this.props.onItemSelect(`/${carouselId}/list/${index}`);
			return;
		}

		this.props.onItemSelect(`/carousels/overrides/${movieId}`);
	}

	sortMoviesByReleaseDate(a, b) {
		// movies no longer in cinemas always go last
		const statusA = Store.get().movieStatuses?.[a?.id];
		const statusB = Store.get().movieStatuses?.[b?.id];

		const aTheaters = statusA?.theaters !== 'unknown';
		const bTheaters = statusB?.theaters !== 'unknown';
		if (aTheaters !== bTheaters) return aTheaters ? -1 : 1;

		const aDate = a.dates?.release || '';
		const bDate = b.dates?.release || '';
		return bDate.localeCompare(aDate);
	}

	togglePinned(e) {
		const { carouselId, dataRoot } = this.props;
		const movieId = e.target.closest('[data-movie]')?.dataset.movie;
		if (!carouselId || !movieId) return;
		
		const path = `${dataRoot || ''}/content/carousels/${carouselId}/pinned`;
		const pinned = jsonPointer.get(Store.get().data, path) !== movieId;
		jsonPointer.setDeep(Store.get().data, path, pinned ? movieId : null);
	}

	render(props) {
		const { carouselId, inCarouselLabel, movies, selectedMovies, sortable } = props;
		if (!movies) return;
		
		const inCarousel = (sortable ? selectedMovies : Object.values(movies || {})) || [];
		const available = sortable && this.availableToAdd || [];

		let sortType = 'pin';
		let ListContainer = 'div';
		if (sortable) {
			sortType = 'custom';
			ListContainer = Sortable;
		}

		return (
			<div class={s.carouselSettings} data-carousel={carouselId}>
				<div class={s.section}>
					<div class={s.sectionName}>{inCarouselLabel || 'In carousel'}</div>
					<ListContainer class={s.list} onChange={this.onCarouselSort} disabled={!canUser('write')}>
						{inCarousel.map(item => (
							<CarouselItem
								customData={item} 
								key={`${carouselId}-carousel-item-${item.movie?.id || item.movie}`}
								movie={typeof item.movie === 'string' ? movies[item.movie] : item.movie}
								onEditClick={this.selectCarouselItem}
								onRemoveClick={sortable && this.removeMovieFromCarousel}
								sortType={sortType}
								togglePinned={sortType === 'pin' && this.togglePinned}
							/>
						))}
					</ListContainer>
				</div>
				{sortable && (
					<div class={s.section}>
						<div class={s.sectionName}>Available to add</div>
						<div class={s.list}>
							{available.map(movie => (
								<CarouselItem
									key={`${carouselId}-available-item-${movie.id}`}
									movie={movie}
									onAddClick={this.addMovieToCarousel}
								/>
							))}
						</div>
					</div>
				)}
			</div>
		);
	}
}